import * as sentry from "@sentry/react";
import { BigNumber, BigNumberish, ethers } from "ethers";
import { config } from "../config";
import { AppRpcProvider } from "../utils";
import { PoolsContract, PoolsContractV2 } from "./contracts";
import { erc20abi, poolsContractAbi, soilTokenAbi } from "./contracts/abi";
import { tokenService } from "./token.service";

const getFeeBalance = async () => PoolsContract.feeBalance();

const getFeeBalanceV2 = async (
  poolsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider,
  token: string
) => PoolsContractV2(poolsContractAddress, provider).feeBalanceInToken(token);

const getPoolContractFees = async () => PoolsContract.poolsContractFee();

const getPoolContractFeesV2 = async (
  poolsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider,
  token: string
) =>
  PoolsContractV2(poolsContractAddress, provider).poolsContractFeeInToken(
    token
  );

const changePoolsContractFee = async (
  fee: BigNumber,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "changePoolsContractFee",
    fee: fee.toString(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
    userAddress: await signer.getAddress(),
  });
  const { wait } = await PoolsContract.connect(signer).changePoolsContractFee(
    fee
  );
  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const changePoolsContractFeeV2 = async (
  poolsContractAddress: string,
  fee: BigNumber,
  signer: ethers.providers.JsonRpcSigner,
  token: string
) => {
  sentry.setContext("Crypto action", {
    name: "changePoolsContractFeeForToken",
    fee: fee.toString(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
    userAddress: await signer.getAddress(),
    token,
  });
  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .changePoolsContractFeeForToken(fee, token);
  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const withdrawFees = async (
  address: string,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "withdrawFees",
    address,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
    userAddress: await signer.getAddress(),
  });
  const { wait } = await PoolsContract.connect(signer).withdrawFees(address);
  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const withdrawFeesV2 = async (
  poolsContractAddress: string,
  address: string,
  signer: ethers.providers.JsonRpcSigner,
  token: string
) => {
  sentry.setContext("Crypto action", {
    name: "withdrawFeesForToken",
    address,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
    userAddress: await signer.getAddress(),
  });
  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .withdrawFees(address, token);
  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const deposit = async (
  USDC_TOKEN_ADDRESS: string,
  poolId: Pool["id"],
  level: PoolLevel["level"],
  amount: BigNumber,
  deadline: string,
  signature: string,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "deposit",
    poolId,
    level,
    amount: amount.toString(),
    deadline,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });
  await tokenService.approveIfNeeded(
    USDC_TOKEN_ADDRESS,
    PoolsContract.address,
    amount,
    signer
  );

  const { wait } = await PoolsContract.connect(signer).deposit(
    poolId,
    level,
    amount,
    deadline,
    signature
  );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const depositV2 = async (
  USDC_TOKEN_ADDRESS: string,
  poolId: Pool["id"],
  level: PoolLevel["level"],
  amount: BigNumber,
  deadline: string,
  signature: string,
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  userAddress: string
) => {
  sentry.setContext("Crypto action", {
    name: "deposit - v2",
    poolId,
    level,
    amount: amount.toString(),
    deadline,
    userAddress,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });
  await tokenService.approveIfNeeded(
    USDC_TOKEN_ADDRESS,
    poolsContractAddress,
    amount,
    signer
  );

  const params = {
    userAddress,
    poolId,
    level,
    amount,
    deadline,
    signature,
  };

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .deposit(params);

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const withdraw = async (
  poolId: Pool["id"],
  level: PoolLevel["level"],
  amount: BigNumber | string,
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  claimSignature: string,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "withdraw",
    poolId,
    level,
    amount: amount.toString(),
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).withdraw(
    poolId,
    level,
    amount,
    rewards,
    soilRewards,
    deadline,
    claimSignature
  );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const withdrawV2 = async (
  poolId: string,
  level: string,
  amount: BigNumber | string,
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  withdrawSignature: string,
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  userAddress: string
) => {
  sentry.setContext("Crypto action", {
    name: "withdraw - v2",
    poolId,
    level,
    amount: amount.toString(),
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const params = {
    userAddress,
    poolId,
    level,
    amount,
    rewards,
    soilRewards,
    deadline,
    withdrawSignature,
  };

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .withdraw(params);

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const withdrawUnlockedTokensV2 = async (
  poolId: string,
  level: string,
  amount: BigNumber | string,
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  signature: string,
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddres: string,
  userAddress: string,
  positionId: string
) => {
  sentry.setContext("Crypto action", {
    name: "withdrawUnlockedTokens",
    poolId,
    level,
    amount: amount.toString(),
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddres,
  });

  const params = {
    userAddress,
    poolId,
    level,
    amount,
    rewards,
    soilRewards,
    deadline,
    withdrawSignature: signature,
  };
  const { wait } = await PoolsContractV2(poolsContractAddres, signer)
    .connect(signer)
    .withdrawUnlockedTokens(params, positionId);

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const claimRewards = async (
  poolId: Pool["id"],
  level: PoolLevel["level"],
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  claimSignature: string,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "claimRewards",
    level,
    poolId,
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).claimRewards(
    poolId,
    level,
    rewards,
    soilRewards,
    deadline,
    claimSignature
  );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const claimRewardsV2 = async (
  poolId: string,
  level: string,
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  claimSignature: string,
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  userAddress: string
) => {
  sentry.setContext("Crypto action", {
    name: "claimRewards - v2",
    poolId,
    level,
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .claimRewards(
      userAddress,
      poolId,
      level,
      rewards,
      soilRewards,
      deadline,
      claimSignature
    );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const upgradePool = async (
  poolId: Pool["id"],
  level: PoolLevel["level"],
  upgradeToLevel: PoolLevel["level"],
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  claimSignature: string,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "upgradePool",
    poolId,
    level,
    upgradeToLevel,
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(
    signer
  ).upgradeToPoolWithHigherYield(
    poolId,
    level,
    upgradeToLevel,
    rewards,
    soilRewards,
    deadline,
    claimSignature
  );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const upgradePoolV2 = async (
  poolId: Pool["id"],
  level: PoolLevel["level"],
  upgradeToLevel: PoolLevel["level"],
  rewards: BigNumber | string,
  soilRewards: BigNumber | string,
  deadline: string,
  claimSignature: string,
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  userAddress: string
) => {
  sentry.setContext("Crypto action", {
    name: "upgradePool - v2",
    poolId,
    level,
    upgradeToLevel,
    rewards: rewards.toString(),
    soilRewards: soilRewards.toString(),
    deadline,
    userAddress,
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .upgradeToPoolWithHigherYield(
      userAddress,
      poolId,
      level,
      upgradeToLevel,
      rewards,
      soilRewards,
      deadline,
      claimSignature
    );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const createPool = async (
  signer: ethers.providers.JsonRpcSigner,
  name: string,
  startTime: BigNumberish,
  endTime: BigNumberish,
  softCap: BigNumberish,
  hardCap: BigNumberish,
  periods: BigNumberish[],
  fixedAPRs: BigNumberish[]
) => {
  sentry.setContext("Crypto action", {
    name: "createPool",
    startTime,
    endTime,
    softCap,
    hardCap,
    periods,
    fixedAPRs,
    poolName: name,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).createPool(
    name,
    startTime,
    endTime,
    softCap,
    hardCap,
    periods,
    fixedAPRs
  );

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const createPoolV2 = async (
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  name: string,
  startTime: BigNumberish,
  endTime: BigNumberish,
  softCap: BigNumberish,
  hardCap: BigNumberish,
  isShort: boolean,
  fixedAPRs: BigNumberish[],
  token: string
) => {
  sentry.setContext("Crypto action", {
    name: "createPool - v2",
    startTime,
    endTime,
    softCap,
    hardCap,
    isShort,
    fixedAPRs,
    poolName: name,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .createPool(
      name,
      token,
      startTime,
      endTime,
      softCap,
      hardCap,
      fixedAPRs,
      isShort
    );

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const updatePool = async (
  signer: ethers.providers.JsonRpcSigner,
  poolId: BigNumberish,
  startTime: BigNumberish,
  endTime: BigNumberish
) => {
  sentry.setContext("Crypto action", {
    name: "updatePool",
    poolId,
    startTime,
    endTime,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).updatePool(
    poolId,
    startTime,
    endTime
  );

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const updatePoolV2 = async (
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  poolId: BigNumberish,
  startTime: BigNumberish,
  endTime: BigNumberish,
  hardCap: BigNumberish
) => {
  sentry.setContext("Crypto action", {
    name: "updatePool - v2",
    poolId,
    startTime,
    endTime,
    hardCap,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .updatePool(poolId, startTime, endTime, hardCap);

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const getUserInfo = (user: string, poolId: BigNumberish, level: BigNumberish) =>
  PoolsContract.userInfo(user, poolId, level);

const getUserInfoV2 = (
  poolsContractAddress: string,
  user: string,
  poolId: BigNumberish,
  level: BigNumberish,
  provider: ethers.providers.JsonRpcProvider
) =>
  PoolsContractV2(poolsContractAddress, provider).userInfo(user, poolId, level);

const withdrawFunds = async (
  target: string,
  amount: ethers.BigNumber,
  signer: ethers.providers.JsonRpcSigner
) => {
  sentry.setContext("Crypto action", {
    name: "withdrawFunds",
    target,
    amount: amount.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).withdrawFunds(
    target,
    amount
  );

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);

  return transactionHash;
};

const withdrawFundsV2 = async (
  poolsContractAddress: string,
  target: string,
  amount: ethers.BigNumber,
  signer: ethers.providers.JsonRpcSigner,
  token: string
) => {
  sentry.setContext("Crypto action", {
    name: "withdrawFunds - v2",
    target,
    amount: amount.toString(),
    token,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .withdrawFunds(target, amount, token);

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);

  return transactionHash;
};

const changeSoilWithdrawLimit = async (
  signer: ethers.providers.JsonRpcSigner,
  newLimit: BigNumber
) => {
  sentry.setContext("Crypto action", {
    name: "changeSoilWithdrawLimit",
    newLimit: newLimit.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).changeSoilRewardsLimit(
    newLimit
  );

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const changeSoilWithdrawLimitV2 = async (
  poolsContractAddress: string,
  signer: ethers.providers.JsonRpcSigner,
  newLimit: BigNumber
) => {
  sentry.setContext("Crypto action", {
    name: "changeSoilWithdrawLimit - v2",
    newLimit: newLimit.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .changeSoilRewardsLimit(newLimit);

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const changeUsdcWithdrawLimit = async (
  signer: ethers.providers.JsonRpcSigner,
  newLimit: BigNumber
) => {
  sentry.setContext("Crypto action", {
    name: "changeUsdcWithdrawLimit",
    newLimit: newLimit.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).changeTokenRewardsLimit(
    newLimit
  );

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const changeUsdcWithdrawLimitV2 = async (
  poolsContractAddress: string,
  signer: ethers.providers.JsonRpcSigner,
  newLimit: BigNumber,
  token: string
) => {
  sentry.setContext("Crypto action", {
    name: "changeUsdcWithdrawLimit - v2",
    newLimit: newLimit.toString(),
    token,
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .changeTokenRewardsLimit(token, newLimit);

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const initPoolsContract = () =>
  new ethers.Contract(
    config.POOLS_CONTRACT_ADDRESS_POLYGON_V1,
    poolsContractAbi,
    AppRpcProvider
  );

const limitOfSoilRewards = async () => {
  const poolsContract = initPoolsContract();
  const soilLimit = await poolsContract.limitOfSoilRewards();
  return soilLimit;
};

const limitOfSoilRewardsV2 = async (
  poolsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider
) => {
  const soilLimit = await PoolsContractV2(poolsContractAddress, provider)
    .connect(provider)
    .limitOfSoilRewards();
  return soilLimit;
};

const limitOfUsdcRewards = async () => {
  const poolsContract = initPoolsContract();
  const usdcLimit = await poolsContract.limitOfTokenRewards();
  return usdcLimit;
};

const limitOfUsdcRewardsV2 = async (
  poolsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider,
  token: string
) => {
  const usdcLimit = await PoolsContractV2(poolsContractAddress, provider)
    .connect(provider)
    .limitOfTokenRewards(token);
  return usdcLimit;
};

const getSoilRewards = async () => {
  const poolsContract = initPoolsContract();
  const soilRewards = await poolsContract.amountOfSoilRewards();
  return soilRewards;
};

const getSoilRewardsV2 = async (
  poolsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider
) => {
  const soilRewards = await PoolsContractV2(poolsContractAddress, provider)
    .connect(provider)
    .amountOfSoilRewards();
  return soilRewards;
};

const getUsdcRewards = async () => {
  const poolsContract = initPoolsContract();
  const usdcRewards = await poolsContract.amountOfTokenRewards();
  return usdcRewards;
};

const getUsdcRewardsV2 = async (
  poolsContractAddress: string,
  provider: ethers.providers.JsonRpcProvider,
  token: string
) => {
  const usdcRewards = await PoolsContractV2(poolsContractAddress, provider)
    .connect(provider)
    .amountOfTokenRewards(token);
  return usdcRewards;
};

const changeAgreementToBeRewardsHolder = async (
  USDC_TOKEN_ADDRESS: string,
  signer: ethers.providers.JsonRpcSigner
) => {
  const SoilTokenContract = new ethers.Contract(
    config.SOIL_TOKEN_ADDRESS,
    soilTokenAbi,
    signer
  );

  const UsdcTokenContract = new ethers.Contract(
    USDC_TOKEN_ADDRESS,
    erc20abi,
    signer
  );

  sentry.setContext("Crypto action", {
    name: "changeAgreementToBeRewardsHolder",
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  await SoilTokenContract.approve(
    config.POOLS_CONTRACT_ADDRESS_POLYGON_V1,
    ethers.constants.MaxUint256
  );
  await UsdcTokenContract.approve(
    config.POOLS_CONTRACT_ADDRESS_POLYGON_V1,
    ethers.constants.MaxUint256
  );

  const { wait } = await PoolsContract.connect(
    signer
  ).changeAgreementToBeRewardsHolder();

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const changeAgreementToBeRewardsHolderV2 = async (
  tokenAddress: string,
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  isPolygon: boolean
) => {
  sentry.setContext("Crypto action", {
    name: "changeAgreementToBeRewardsHolder - v2",
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  if (isPolygon) {
    const SoilTokenContract = new ethers.Contract(
      config.SOIL_TOKEN_ADDRESS,
      soilTokenAbi,
      signer
    );

    await SoilTokenContract.approve(
      poolsContractAddress,
      ethers.constants.MaxUint256
    );
  }

  const tokenContract = new ethers.Contract(tokenAddress, erc20abi, signer);

  await tokenContract.approve(
    poolsContractAddress,
    ethers.constants.MaxUint256
  );

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .changeAgreementToBeRewardsHolder();

  const tx = await wait(config.CONFIRMATION_BLOCKS);
  return tx;
};

const sendUsdcRewardsToContract = async (
  signer: ethers.providers.JsonRpcSigner,
  from: string,
  amount: BigNumber
) => {
  sentry.setContext("Crypto action", {
    name: "sendUsdcRewardsToContract",
    from,
    amount: amount.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).sendTokenRewards(
    from,
    amount
  );

  const tx = await wait(1);
  return tx;
};

const sendTokenRewardsToContractV2 = async (
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  from: string,
  amount: BigNumber,
  token: string
) => {
  sentry.setContext("Crypto action", {
    name: "sendTokenRewardsToContract - v2",
    from,
    amount: amount.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .sendTokenRewards(from, amount, token);

  const tx = await wait(1);
  return tx;
};

const sendSoilRewardsToContract = async (
  signer: ethers.providers.JsonRpcSigner,
  from: string,
  amount: BigNumber
) => {
  sentry.setContext("Crypto action", {
    name: "sendSoilRewardsToContract",
    from,
    amount: amount.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress: PoolsContract.address,
  });

  const { wait } = await PoolsContract.connect(signer).sendSoilRewards(
    from,
    amount
  );

  const tx = await wait(1);
  return tx;
};

const sendSoilRewardsToContractV2 = async (
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  from: string,
  amount: BigNumber
) => {
  sentry.setContext("Crypto action", {
    name: "sendSoilRewardsToContract - v2",
    from,
    amount: amount.toString(),
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .sendSoilRewards(from, amount);

  const tx = await wait(1);
  return tx;
};

const redepositV2 = async (
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  userAddress: string,
  poolId: BigNumberish,
  level: BigNumberish,
  rewards: BigNumberish,
  deadline: string,
  signature: string
) => {
  sentry.setContext("Crypto action", {
    name: "redeposit - v2",
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
    poolId,
    level,
    rewards,
    deadline,
  });

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .redeposit(userAddress, poolId, level, rewards, deadline, signature);

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

const unlockTokensV2 = async (
  signer: ethers.providers.JsonRpcSigner,
  poolsContractAddress: string,
  userAddress: string,
  poolId: BigNumberish,
  level: BigNumberish,
  amount: BigNumberish,
  deadline: string,
  signature: string
) => {
  sentry.setContext("Crypto action", {
    name: "unlockTokens - v2",
    userAddress: await signer.getAddress(),
    chainId: await signer.provider.getNetwork().then((n) => n.chainId),
    poolsContractAddress,
    poolId,
    level,
    amount,
    deadline,
  });

  const unlockParams = {
    userAddress,
    poolId,
    level,
    amount,
    deadline,
    unlockSignature: signature,
  };

  const { wait } = await PoolsContractV2(poolsContractAddress, signer)
    .connect(signer)
    .unlockTokens(unlockParams);

  const { transactionHash } = await wait(config.CONFIRMATION_BLOCKS);
  return transactionHash;
};

export const poolsContractService = {
  getFeeBalance,
  getFeeBalanceV2,
  getPoolContractFees,
  getPoolContractFeesV2,
  changePoolsContractFee,
  changePoolsContractFeeV2,
  withdrawFees,
  withdrawFeesV2,
  deposit,
  depositV2,
  withdraw,
  withdrawV2,
  claimRewards,
  claimRewardsV2,
  upgradePool,
  upgradePoolV2,
  createPool,
  createPoolV2,
  getUserInfo,
  getUserInfoV2,
  updatePool,
  updatePoolV2,
  withdrawFunds,
  withdrawFundsV2,
  changeSoilWithdrawLimit,
  changeSoilWithdrawLimitV2,
  changeUsdcWithdrawLimit,
  changeUsdcWithdrawLimitV2,
  limitOfSoilRewards,
  limitOfSoilRewardsV2,
  limitOfUsdcRewards,
  limitOfUsdcRewardsV2,
  getSoilRewards,
  getSoilRewardsV2,
  getUsdcRewards,
  getUsdcRewardsV2,
  changeAgreementToBeRewardsHolder,
  changeAgreementToBeRewardsHolderV2,
  sendSoilRewardsToContract,
  sendSoilRewardsToContractV2,
  sendUsdcRewardsToContract,
  sendTokenRewardsToContractV2,
  redepositV2,
  unlockTokensV2,
  withdrawUnlockedTokensV2,
};
